import React, { useState, useEffect } from "react";
import { 
    BarChart, 
    Bar, 
    XAxis, 
    YAxis, 
    CartesianGrid, 
    Tooltip as RechartsTooltip, 
    ResponsiveContainer, 
    PieChart, 
    Pie, 
    Cell,
    LineChart,
    Line,
    Legend
} from 'recharts';
import { 
    TrendingUp, 
    Clock, 
    ShieldAlert, 
    Briefcase, 
    ChevronRight,
    ArrowUpRight,
    Search,
    Filter,
    Download,
    Calendar,
    MousePointer2,
    Cpu,
    Coins,
    Zap,
    AlertCircle,
    CheckCircle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useApiClient } from "@/hooks/use-api-client";
import { toast } from "sonner";

// --- Design Tokens ---
const COLORS = {
    primary: "#2563eb",   // Blue 600
    secondary: "#4f46e5", // Indigo 600
    success: "#10b981",   // Emerald 500
    warning: "#f59e0b",   // Amber 500
    danger: "#ef4444",    // Red 500
    purple: "#8b5cf6",    // Violet 500
    slate: ["#f8fafc", "#f1f5f9", "#e2e8f0", "#cbd5e1", "#94a3b8"]
};

const RISK_COLORS = {
    "Alto": COLORS.danger,
    "Medio": COLORS.warning,
    "Bajo": COLORS.success,
    "Crítico": "#7f1d1d"  // Dark Red
};

export default function ROIDashboard() {
    const apiClient = useApiClient();
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState<any>(null);
    const [activeTab, setActiveTab] = useState<'roi' | 'tokens'>('roi');

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        try {
            const response = await (apiClient as any).get("/api/v1/agents/roi-stats");
            setStats(response.data);
        } catch (error) {
            console.error("Error fetching ROI stats:", error);
            toast.error("Error al cargar las métricas de ROI");
        } finally {
            setLoading(false);
        }
    };

    if (loading || !stats) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh]">
                <div className="w-12 h-12 border-4 border-blue-600/20 border-t-blue-600 rounded-full animate-spin mb-4" />
                <p className="text-slate-500 font-medium">Calculando ROI Estratégico...</p>
            </div>
        );
    }

    const fmtNumber = (num: number) => {
        return num.toLocaleString('es-CO');
    };

    return (
        <div className="p-8 max-w-[1600px] mx-auto animate-in fade-in duration-700">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8">
                <div>
                    <div className="flex items-center gap-2 mb-2">
                        <Badge className="bg-blue-600/10 text-blue-600 hover:bg-blue-600/20 border-none px-3 py-1 font-bold">
                            Capa de Inteligencia Corporativa
                        </Badge>
                    </div>
                    <h1 className="text-4xl font-black text-slate-900 tracking-tight flex items-center gap-3">
                        Panel de Control Operativo <TrendingUp className="w-8 h-8 text-blue-600" />
                    </h1>
                    <p className="text-slate-500 mt-1 font-medium">Seguimiento de ROI en horas hombre y métricas de consumo de LLM.</p>
                </div>
                
                <div className="flex items-center gap-3">
                    <Button variant="outline" className="rounded-2xl gap-2 font-bold border-slate-200">
                        <Calendar className="w-4 h-4" /> Últimos 30 días
                    </Button>
                    <Button className="rounded-2xl gap-2 bg-slate-900 hover:bg-slate-800 text-white font-bold shadow-xl">
                        <Download className="w-4 h-4" /> Exportar Informe
                    </Button>
                </div>
            </div>

            {/* Custom Interactive Tabs */}
            <div className="flex border-b border-slate-100 mb-8 gap-6">
                <button 
                    onClick={() => setActiveTab('roi')}
                    className={`pb-4 px-2 font-extrabold text-lg transition-all ${
                        activeTab === 'roi' 
                        ? 'border-b-4 border-blue-600 text-blue-600 scale-102' 
                        : 'text-slate-400 hover:text-slate-600'
                    }`}
                >
                    Retorno de Inversión (ROI)
                </button>
                <button 
                    onClick={() => setActiveTab('tokens')}
                    className={`pb-4 px-2 font-extrabold text-lg transition-all ${
                        activeTab === 'tokens' 
                        ? 'border-b-4 border-blue-600 text-blue-600 scale-102' 
                        : 'text-slate-400 hover:text-slate-600'
                    }`}
                >
                    Monitoreo de Tokens y Recursos
                </button>
            </div>

            {activeTab === 'roi' ? (
                <>
                    {/* Hero ROI Metrics Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                        <Card className="rounded-[2.5rem] border-slate-100 shadow-sm overflow-hidden bg-gradient-to-br from-blue-600 to-indigo-700 text-white">
                            <CardContent className="p-8">
                                <div className="flex justify-between items-start mb-4">
                                    <div className="p-3 bg-white/10 rounded-2xl backdrop-blur-md">
                                        <Clock className="w-6 h-6 text-blue-100" />
                                    </div>
                                    <div className="flex items-center gap-1 text-[10px] font-black bg-emerald-500/20 text-emerald-300 px-2 py-1 rounded-full border border-emerald-500/30">
                                        <ArrowUpRight className="w-3 h-3" /> +12% vs mes ant.
                                    </div>
                                </div>
                                <div className="text-5xl font-black mb-1">{stats.summary.total_hours}h</div>
                                <div className="text-blue-100 font-bold text-sm uppercase tracking-wider">Tiempo Total Ahorrado</div>
                                <div className="mt-6 pt-4 border-t border-white/10 text-xs text-blue-200 font-medium italic">
                                    Equivale a {Math.round(stats.summary.total_hours / 8)} jornadas laborales de un abogado senior.
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="rounded-[2.5rem] border-slate-100 shadow-sm overflow-hidden bg-white">
                            <CardContent className="p-8">
                                <div className="flex justify-between items-start mb-4">
                                    <div className="p-3 bg-amber-50 rounded-2xl">
                                        <ShieldAlert className="w-6 h-6 text-amber-500" />
                                    </div>
                                </div>
                                <div className="text-5xl font-black text-slate-900 mb-1">{stats.summary.high_risks_mitigated}</div>
                                <div className="text-slate-500 font-bold text-sm uppercase tracking-wider">Riesgos Críticos Mitigados</div>
                                <div className="mt-6 flex gap-2">
                                    <Badge className="bg-red-50 text-red-600 p-0 px-2 text-[10px] font-black border-none">ALTO IMPACTO</Badge>
                                    <Badge className="bg-slate-50 text-slate-500 p-0 px-2 text-[10px] font-black border-none">CUMPLIMIENTO</Badge>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="rounded-[2.5rem] border-slate-100 shadow-sm overflow-hidden bg-white">
                            <CardContent className="p-8">
                                <div className="flex justify-between items-start mb-4">
                                    <div className="p-3 bg-emerald-50 rounded-2xl">
                                        <MousePointer2 className="w-6 h-6 text-emerald-500" />
                                    </div>
                                </div>
                                <div className="text-5xl font-black text-slate-900 mb-1">{stats.summary.total_executions}</div>
                                <div className="text-slate-500 font-bold text-sm uppercase tracking-wider">Gestiones Automatizadas</div>
                                <div className="mt-6 text-xs text-slate-400 font-medium">
                                    Consumo integrado con sistema de enrutamiento dinámico.
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Graphs Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                        {/* Savings by Sector */}
                        <Card className="rounded-[2.5rem] border-slate-100 shadow-xl shadow-slate-200/50 bg-white">
                            <CardHeader className="p-8 pb-0">
                                <CardTitle className="text-xl font-black text-slate-900">Ahorro por Sector Estratégico</CardTitle>
                                <CardDescription className="text-slate-500 font-medium">Distribución de minutos ahorrados por industria.</CardDescription>
                            </CardHeader>
                            <CardContent className="p-8 h-[400px]">
                                {stats.by_sector && stats.by_sector.length > 0 ? (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={stats.by_sector} layout="vertical" margin={{ left: 20 }}>
                                            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                                            <XAxis type="number" hide />
                                            <YAxis 
                                                dataKey="sector" 
                                                type="category" 
                                                axisLine={false} 
                                                tickLine={false} 
                                                tick={{ fill: '#64748b', fontWeight: 700, fontSize: 12 }} 
                                            />
                                            <RechartsTooltip 
                                                cursor={{ fill: '#f8fafc' }}
                                                contentStyle={{ borderRadius: '1rem', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                                            />
                                            <Bar 
                                                dataKey="minutes" 
                                                fill={COLORS.primary} 
                                                radius={[0, 10, 10, 0]} 
                                                barSize={24}
                                            >
                                                {stats.by_sector.map((entry: any, index: number) => (
                                                    <Cell key={`cell-${index}`} fill={index === 0 ? COLORS.primary : COLORS.secondary} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <div className="flex items-center justify-center h-full text-slate-400 font-bold">No hay datos de sectores disponibles</div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Risk Distribution */}
                        <Card className="rounded-[2.5rem] border-slate-100 shadow-xl shadow-slate-200/50 bg-white">
                            <CardHeader className="p-8 pb-0">
                                <CardTitle className="text-xl font-black text-slate-900">Severidad de Intervención</CardTitle>
                                <CardDescription className="text-slate-500 font-medium">Clasificación de casos atendidos por nivel de riesgo mitigado.</CardDescription>
                            </CardHeader>
                            <CardContent className="p-8 h-[400px] flex items-center justify-center">
                                {stats.risk_distribution && stats.risk_distribution.length > 0 ? (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={stats.risk_distribution}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={80}
                                                outerRadius={120}
                                                paddingAngle={8}
                                                dataKey="count"
                                                nameKey="level"
                                            >
                                                {stats.risk_distribution.map((entry: any, index: number) => (
                                                    <Cell key={`cell-${index}`} fill={RISK_COLORS[entry.level as keyof typeof RISK_COLORS] || COLORS.slate[3]} />
                                                ))}
                                            </Pie>
                                            <RechartsTooltip />
                                            <Legend verticalAlign="bottom" height={36} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <div className="flex items-center justify-center h-full text-slate-400 font-bold">No hay datos de riesgo disponibles</div>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    {/* ROI Timeline */}
                    <Card className="rounded-[2.5rem] border-slate-100 shadow-xl shadow-slate-200/50 bg-white mb-8">
                        <CardHeader className="p-8 pb-0">
                            <CardTitle className="text-xl font-black text-slate-900">Evolución de Valor Acumulado</CardTitle>
                            <CardDescription className="text-slate-500 font-medium">Crecimiento diario del ROI operativo en minutos ahorrados.</CardDescription>
                        </CardHeader>
                        <CardContent className="p-8 h-[300px]">
                            {stats.timeline && stats.timeline.length > 0 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={stats.timeline}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                        <XAxis 
                                            dataKey="date" 
                                            axisLine={false} 
                                            tickLine={false} 
                                            tick={{ fill: '#94a3b8', fontSize: 10 }}
                                            tickFormatter={(val) => val ? val.split('-')[2] : ''}
                                        />
                                        <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 10 }} />
                                        <RechartsTooltip 
                                            contentStyle={{ borderRadius: '1rem', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                                        />
                                        <Line 
                                            type="monotone" 
                                            dataKey="minutes" 
                                            stroke={COLORS.primary} 
                                            strokeWidth={4} 
                                            dot={{ fill: COLORS.primary, r: 6, strokeWidth: 2, stroke: '#fff' }}
                                            activeDot={{ r: 8, strokeWidth: 0 }}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="flex items-center justify-center h-full text-slate-400 font-bold">No hay datos de evolución disponibles</div>
                            )}
                        </CardContent>
                    </Card>
                </>
            ) : (
                <>
                    {/* Hero Resource Metrics Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                        <Card className="rounded-[2.5rem] border-slate-100 shadow-sm overflow-hidden bg-gradient-to-br from-indigo-600 to-purple-700 text-white">
                            <CardContent className="p-8">
                                <div className="flex justify-between items-start mb-4">
                                    <div className="p-3 bg-white/10 rounded-2xl backdrop-blur-md">
                                        <Cpu className="w-6 h-6 text-indigo-100" />
                                    </div>
                                </div>
                                <div className="text-4xl font-black mb-1">{fmtNumber(stats.summary.total_tokens)}</div>
                                <div className="text-indigo-100 font-bold text-sm uppercase tracking-wider">Tokens Consumidos</div>
                                <div className="mt-6 pt-4 border-t border-white/10 text-xs text-indigo-200 font-medium italic">
                                    Mide el volumen de procesamiento de texto (entrada y salida) del motor de IA.
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="rounded-[2.5rem] border-slate-100 shadow-sm overflow-hidden bg-white">
                            <CardContent className="p-8">
                                <div className="flex justify-between items-start mb-4">
                                    <div className="p-3 bg-indigo-50 rounded-2xl">
                                        <Coins className="w-6 h-6 text-indigo-600" />
                                    </div>
                                    <Badge className="bg-emerald-50 text-emerald-600 border-none font-extrabold text-[10px]">EFICIENTE</Badge>
                                </div>
                                <div className="text-4xl font-black text-slate-900 mb-1">${stats.summary.total_cost.toFixed(4)} USD</div>
                                <div className="text-slate-500 font-bold text-sm uppercase tracking-wider">Costo Estimado Acumulado</div>
                                <div className="mt-6 text-xs text-slate-400 font-medium">
                                    Costo calculado dinámicamente según el modelo y tarifas de API vigentes.
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="rounded-[2.5rem] border-slate-100 shadow-sm overflow-hidden bg-white">
                            <CardContent className="p-8">
                                <div className="flex justify-between items-start mb-4">
                                    <div className="p-3 bg-indigo-50 rounded-2xl">
                                        <Zap className="w-6 h-6 text-indigo-600" />
                                    </div>
                                </div>
                                <div className="text-4xl font-black text-slate-900 mb-1">
                                    {stats.summary.total_executions > 0 
                                        ? (stats.summary.total_tokens / stats.summary.total_executions).toFixed(0)
                                        : 0
                                    }
                                </div>
                                <div className="text-slate-500 font-bold text-sm uppercase tracking-wider">Tokens Promedio / Ejecución</div>
                                <div className="mt-6 text-xs text-slate-400 font-medium">
                                    Carga promedio de contexto legal por interacción procesada.
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Breakdown and Optimizations Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-8">
                        {/* Token Breakdown by Agent */}
                        <Card className="rounded-[2.5rem] border-slate-100 shadow-xl shadow-slate-200/50 bg-white lg:col-span-7">
                            <CardHeader className="p-8">
                                <CardTitle className="text-xl font-black text-slate-900">Consumo por Agente Jurídico</CardTitle>
                                <CardDescription className="text-slate-500 font-medium">Historial y estadísticas de tokens consumidos por cada copiloto legal.</CardDescription>
                            </CardHeader>
                            <CardContent className="p-8 pt-0">
                                {stats.by_agent && stats.by_agent.length > 0 ? (
                                    <div className="space-y-6">
                                        {stats.by_agent.map((agent: any, idx: number) => {
                                            const totalTokens = stats.summary.total_tokens || 1;
                                            const percent = Math.min((agent.tokens / totalTokens) * 100, 100);
                                            return (
                                                <div key={agent.agent_id} className="group">
                                                    <div className="flex justify-between text-sm mb-1.5">
                                                        <span className="font-extrabold text-slate-800">{agent.name}</span>
                                                        <span className="font-extrabold text-slate-500">
                                                            {agent.tokens.toLocaleString()} tokens · ${agent.cost.toFixed(4)} USD
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-4">
                                                        <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
                                                            <div 
                                                                className="h-full rounded-full transition-all duration-500"
                                                                style={{ 
                                                                    width: `${percent}%`,
                                                                    backgroundColor: idx === 0 ? COLORS.primary : idx === 1 ? COLORS.secondary : COLORS.purple 
                                                                }}
                                                            />
                                                        </div>
                                                        <span className="text-xs font-black text-slate-700 w-10 text-right">
                                                            {percent.toFixed(1)}%
                                                        </span>
                                                    </div>
                                                    <div className="text-[10px] text-slate-400 mt-1 font-bold">
                                                        {agent.executions} ejecuciones
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center justify-center py-12 text-slate-400 font-bold">
                                        <Cpu className="w-12 h-12 text-slate-200 mb-3" />
                                        Aún no hay ejecuciones registradas.
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Optimization & Warnings */}
                        <Card className="rounded-[2.5rem] border-slate-100 shadow-xl shadow-slate-200/50 bg-white lg:col-span-5 flex flex-col justify-between">
                            <div>
                                <CardHeader className="p-8 pb-4">
                                    <CardTitle className="text-xl font-black text-slate-900">Recomendaciones de Optimización</CardTitle>
                                    <CardDescription className="text-slate-500 font-medium">Alertas y sugerencias inteligentes de rendimiento de IA.</CardDescription>
                                </CardHeader>
                                <CardContent className="p-8 pt-0 space-y-4">
                                    {stats.optimization_insights && stats.optimization_insights.map((insight: any, idx: number) => (
                                        <div 
                                            key={idx} 
                                            className={`p-5 rounded-3xl border flex items-start gap-4 ${
                                                insight.severity === 'warning' 
                                                ? 'bg-amber-50/50 border-amber-100' 
                                                : insight.severity === 'info' 
                                                ? 'bg-blue-50/50 border-blue-100' 
                                                : 'bg-emerald-50/50 border-emerald-100'
                                            }`}
                                        >
                                            {insight.severity === 'warning' ? (
                                                <AlertCircle className="w-6 h-6 text-amber-500 shrink-0 mt-0.5" />
                                            ) : insight.severity === 'info' ? (
                                                <AlertCircle className="w-6 h-6 text-blue-500 shrink-0 mt-0.5" />
                                            ) : (
                                                <CheckCircle className="w-6 h-6 text-emerald-500 shrink-0 mt-0.5" />
                                            )}
                                            <div>
                                                <h5 className={`font-black text-sm mb-1 ${
                                                    insight.severity === 'warning' 
                                                    ? 'text-amber-800' 
                                                    : insight.severity === 'info' 
                                                    ? 'text-blue-800' 
                                                    : 'text-emerald-800'
                                                }`}>
                                                    {insight.title}
                                                </h5>
                                                <p className="text-xs font-medium text-slate-500 leading-relaxed">
                                                    {insight.description}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </CardContent>
                            </div>
                            
                            <div className="p-8 pt-0 border-t border-slate-50 mt-auto">
                                <div className="mt-6 p-5 rounded-3xl bg-slate-50 border border-slate-100 flex justify-between items-center">
                                    <div>
                                        <p className="text-xs font-black text-slate-500 uppercase tracking-widest">Enrutamiento Activo</p>
                                        <p className="text-sm font-bold text-slate-800 mt-1">Fallback Model Manager CREG</p>
                                    </div>
                                    <Badge className="bg-blue-600/10 text-blue-600 border-none font-extrabold px-3 py-1">Auto-Scaling</Badge>
                                </div>
                            </div>
                        </Card>
                    </div>

                    {/* Heavy Ingestion Processes */}
                    <Card className="rounded-[2.5rem] border-slate-100 shadow-xl shadow-slate-200/50 bg-white mb-8">
                        <CardHeader className="p-8">
                            <CardTitle className="text-xl font-black text-slate-900">Procesos de Mayor Consumo</CardTitle>
                            <CardDescription className="text-slate-500 font-medium">Logs de las 5 sesiones con mayor consumo de tokens (Ingesta Pesada).</CardDescription>
                        </CardHeader>
                        <CardContent className="p-8 pt-0 overflow-x-auto">
                            {stats.heavy_processes && stats.heavy_processes.length > 0 ? (
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="border-b border-slate-100">
                                            <th className="pb-4 font-black text-sm text-slate-400">ID de Sesión</th>
                                            <th className="pb-4 font-black text-sm text-slate-400">Agente</th>
                                            <th className="pb-4 font-black text-sm text-slate-400">Sector</th>
                                            <th className="pb-4 font-black text-sm text-slate-400">Tokens Usados</th>
                                            <th className="pb-4 font-black text-sm text-slate-400">Costo Estimado</th>
                                            <th className="pb-4 font-black text-sm text-slate-400">Fecha/Hora</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {stats.heavy_processes.map((proc: any) => (
                                            <tr key={proc.session_id} className="border-b border-slate-50 last:border-0 group hover:bg-slate-50/50">
                                                <td className="py-4 font-bold text-sm text-slate-600 truncate max-w-[200px]">{proc.session_id}</td>
                                                <td className="py-4 font-extrabold text-sm text-slate-800">{proc.agent_name}</td>
                                                <td className="py-4 font-bold text-sm text-slate-500">
                                                    <Badge className="bg-slate-100 text-slate-600 border-none font-bold text-xs">{proc.sector}</Badge>
                                                </td>
                                                <td className="py-4 font-black text-sm text-indigo-600">{proc.tokens.toLocaleString()}</td>
                                                <td className="py-4 font-extrabold text-sm text-slate-700">${proc.cost.toFixed(4)} USD</td>
                                                <td className="py-4 font-bold text-xs text-slate-400">{new Date(proc.timestamp).toLocaleString('es-CO')}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <div className="flex flex-col items-center justify-center py-12 text-slate-400 font-bold">
                                    Aún no hay procesos con consumo registrados.
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </>
            )}

            {/* Actionable Footer */}
            <div className="bg-slate-900 rounded-[2.5rem] p-8 text-white flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-6">
                    <div className="w-16 h-16 rounded-3xl bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-600/20">
                        <Briefcase className="w-8 h-8 text-white" />
                    </div>
                    <div>
                        <h4 className="text-lg font-black uppercase tracking-widest text-blue-400">Estado de Operación</h4>
                        <p className="text-slate-300 font-medium">Su ecosistema de IA se encuentra balanceado. Monitoree tokens frecuentemente para optimizar costos de API.</p>
                    </div>
                </div>
                <Button className="bg-white text-slate-900 hover:bg-slate-100 rounded-2xl px-8 py-6 h-auto font-black flex items-center gap-2 group">
                    Configurar Alertas de Consumo <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
            </div>
        </div>
    );
}
